import '@adornis/authentication/saml-client/client/saml-federation-login.js';
import { logger } from '@adornis/base/logging.js';
import { type ValueEvent } from '@adornis/base/utilTypes.js';
import { getSettingByID } from '@adornis/buchungsheld-db/api/settings.js';
import { getBuchungsheldAuthentication } from '@adornis/buchungsheld-db/authentication/buchungsheld-authentication.js';
import { config } from '@adornis/buchungsheld-db/config.js';
import { isHexColor } from '@adornis/buchungsheld-db/entities/ColorDefinitions.js';
import { GLOBAL_SETTINGS_ID } from '@adornis/buchungsheld-db/entities/settings.js';
import { type BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { event } from '@adornis/chemistry/decorators/event.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { searchParamsSignal } from '@adornis/router/client/routing-state-signal.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import '@adornis/user-forms/x-login-input.js';
import { subscribeCurrentUser, updateCurrentUser } from '@adornis/users/client/currentUser.js';
import { AmbiguousLoginError, InvalidCredentialsError } from '@adornis/users/db/errors.js';
import { emailRegexCheck } from '@adornis/users/db/validators.js';
import { type BaseError } from '@adornis/validation/errors/BaseError.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { choose } from 'lit/directives/choose.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { buttonColorStyle, outlineButtonColorStyle, resolvedButtonFontColor } from '../buttonColorStyles.js';
import './bh-component-action-otp-login.js';

/**
 * @element bh-login
 */
@customElement('bh-login')
export class BHLogin extends ChemistryLitElement {
  @event @property() mode?: 'Login_only_with_mail' | 'token_validation';
  @property({ type: Boolean, attribute: 'hide-back-button' }) hideBackButton?: boolean;

  @state() private readonly _institutionID = new RXController(this, routingState.searchParams.get('institution'));
  @state() private readonly _translation = new TranslationController<BHTranslationDictionary>(this);

  @state() private readonly _globalColorSettings = new RXController(
    this,
    getSettingByID(GLOBAL_SETTINGS_ID)({
      colorDefinitions: {
        loginColorDefinitions: {
          buttonColor: 1,
          buttonFontColor: 1,
          outlineButtonColor: 1,
        },
      },
    }),
  );

  // Backend-customizable colors of the login page; every button falls back to
  // its default appearance when a color is not configured.
  private get _loginColors() {
    return this._globalColorSettings.value?.colorDefinitions?.loginColorDefinitions;
  }

  // The DFN/SAML login button lives inside <saml-federation-login>, which is
  // rendered in light DOM, so its <x-button> is reachable from this component's
  // shadow root via a scoped <style>. The buttonColorStyle() directive only sets
  // host inline styles and cannot reach it. !important overrides x-button's
  // normal :host background regardless of shadow-cascade specifics. The main
  // login button is the filled one (x-button reflects mode="action" by default),
  // unlike the secondary "select institution" links (mode="text"); the disabled
  // placeholder is excluded via :not([disabled]). These assumptions were
  // verified against @adornis/authentication@23.3.8 — re-check on major bumps.
  private _samlButtonColorStyleTag() {
    // These values come from the DB and are written into CSS text, so only
    // plain hex colors (all the color editor produces) are accepted — anything
    // else could inject arbitrary CSS into the public login page.
    const buttonColor = this._loginColors?.buttonColor;
    const bg = isHexColor(buttonColor) ? buttonColor : undefined;
    const fontColor = resolvedButtonFontColor(this._loginColors);
    const fg = isHexColor(fontColor) ? fontColor : undefined;
    if (!bg && !fg) return nothing;
    const rules = [
      bg ? `background: ${bg} !important; border-color: ${bg} !important;` : '',
      fg ? `color: ${fg} !important;` : '',
    ].join(' ');
    // Single, whole-content binding — Lit only reliably supports one binding
    // inside a <style> element.
    const styleText = `saml-federation-login x-button[mode="action"]:not([disabled]) { ${rules} }`;
    return html`<style>
      ${styleText}
    </style>`;
  }

  @state() private errorMessage = '';

  @state() private _email = '';
  password = '';

  private get _hasExtraLoginMethods() {
    return config.get('SAML_IMPLEMENTATION');
  }

  override connectedCallback() {
    super.connectedCallback();

    const errorMessage = routingState.searchParams.getOnce('error_message');
    if (errorMessage === 'non_saml_account_already_exists') {
      XSnackbar.show(
        "Diese E-Mail Adresse ist bereits einem 'nicht-Shibboleth' Konto zugeordnet. Bitte melden Sie sich mit ihrer E-Mail Adresse an",
      );
      return;
    }

    if (!this._hasExtraLoginMethods) this.mode = 'Login_only_with_mail';
    const samlError = searchParamsSignal.value.get('error_message');
    if (samlError) {
      if (samlError.includes('E-Mail')) {
        this.errorMessage =
          'Der Login ist derzeit nicht möglich, da Ihre E-Mail-Adresse nicht vom Identity Provider übermittelt wurde. Dies ist ein Problem des Identity Providers (z.B. HRZ) und kann weder von Ihnen noch von uns behoben werden. Bitte wenden Sie sich an den IT-Support Ihrer Hochschule.';
      } else {
        this.errorMessage = samlError;
      }
    }
  }
  clear() {
    this.mode = undefined;
    this._email = '';
  }

  override render() {
    if (!this.mode) return this._selectLoginMethod();

    return html`
      <x-flex space="md">
        ${choose(
          this.mode,
          [
            ['Login_only_with_mail', () => this.renderLoginOnlyWithMail()],
            ['token_validation', () => this.renderValidateToken()],
          ],
          () => this._selectLoginMethod(),
        )}
        ${
          !this.hideBackButton
            ? html`
                <x-button
                  ${outlineButtonColorStyle(this._loginColors)}
                  mode="outline"
                  icon="west"
                  @click=${() => {
                    this.mode = undefined;
                  }}
                >
                  ${this._translation.translate('LOGIN_PAGE', 'back')}
                </x-button>
              `
            : nothing
        }
      </x-flex>
    `;
  }

  protected _selectLoginMethod() {
    const saml = () => {
      const href = location.href.replace('step=auth', 'step=personal');
      const usePopup = config.get('SAML_USE_POPUP') || location.origin !== new URL(baseConfig.get('ROOT_URL')).origin;
      return html`
        ${this._samlButtonColorStyleTag()}
        <saml-federation-login
          .preselectedIdP=${config.get('SAML_PRESELECTED_IDP')}
          .successRedirect=${new URL(href, baseConfig.get('ROOT_URL')).href}
          ?use-popup=${usePopup}
          @login-state-changed=${(
            event: CustomEvent<{ success: true; error: null } | { success: false; error: Error }>,
          ) => {
            if (event.detail.success) {
              routingState.searchParams.set('step', 'personal');
            } else {
              this.errorMessage = event.detail.error?.message;
            }
          }}
        ></saml-federation-login>
        <x-hr></x-hr>
      `;
    };
    return html`
      <x-flex space-between space="lg" w100 crossaxis-center>
        <x-flex space="lg" flex>
          <x-h1 flex ${css({ textAlign: 'center', padding: '30px', maxWidth: '20ch', margin: 'auto' })}
            >${this._translation.translate('LOGIN')}</x-h1
          >
          ${
            this.errorMessage
              ? html`<x-text
                  tone="error"
                  ${css({ maxWidth: '50ch', margin: 'auto', textAlign: 'center', fontSize: '0.875rem' })}
                >
                  ${this.errorMessage}
                </x-text>`
              : nothing
          }
          ${
            config.get('SAML_IMPLEMENTATION') // deactivate saml login for now
              ? saml()
              : nothing
          }
          <tr-button ${buttonColorStyle(this._loginColors)} @click=${() => (this.mode = 'Login_only_with_mail')}>
            ${this._translation.translate('LOGIN_PAGE', 'loginWithMail')}
          </tr-button>
        </x-flex>
      </x-flex>
    `;
  }

  loginWithMailRenderHelper({
    withoutPassword = false,
    emailOnlyLoginCallback,
    emailAndPasswordLoginCallback,
  }: {
    withoutPassword?: boolean;
    emailOnlyLoginCallback?: (email: string) => Promise<any>;
    emailAndPasswordLoginCallback: (email: string, password: string) => Promise<any>;
  }) {
    if (withoutPassword && !emailOnlyLoginCallback)
      throw new Error('Cannot use login render helper without password but not provide an email only login callback');

    let email = '';

    const validateEmailAndSetError = (email: string) => {
      if (!emailRegexCheck(email)) {
        logger.error({ email }, 'Did not enter valid mail address');
        this._mailError = this._translation.translate('LOGIN_PAGE', 'enterValidMailAddress');
        this.requestUpdate();
        return false;
      }
      return true;
    };

    return html`
      <x-flex space="lg" w100 crossaxis-center>
        <x-flex space="sm" ${css({ width: '250px' /* hardcoded width for x-overlay */ })}>
          <tr-login-input
            type="email"
            id="username"
            icon="mail"
            placeholder=${this._translation.translate('field', 'email')}
            @value-changed=${(e: ValueEvent<string>) => {
              const trimmed = e.detail.value?.trim() ?? '';
              if (this._email) {
                this._email = trimmed;
              }
              email = trimmed;
            }}
            @submit=${() => {
              if (!this._email) {
                if (!validateEmailAndSetError(email)) return;
                this._email = email;
              } else {
                emailAndPasswordLoginCallback(this._email, this.password);
              }
            }}
          ></tr-login-input>

          ${
            this._email
              ? html`
                  <tr-login-input
                    type="password"
                    id="password"
                    icon="password"
                    placeholder=${this._translation.translate('field', 'password')}
                    @value-changed=${(e: ValueEvent<string>) => (this.password = e.detail.value)}
                    @submit=${() => emailAndPasswordLoginCallback(this._email, this.password)}
                  ></tr-login-input>
                `
              : nothing
          }
          ${this._mailError ? html` <x-text tone="error" ${css({})}> ${this._mailError} </x-text> ` : nothing}
          ${this.errorMessage ? html` <x-text tone="error" ${css({})}> ${this.errorMessage} </x-text> ` : nothing}
          ${
            this._email
              ? html`
                  <tr-button
                    ${buttonColorStyle(this._loginColors)}
                    @click=${() => emailAndPasswordLoginCallback(this._email, this.password)}
                  >
                    ${this._translation.translate('LOGIN')}
                  </tr-button>
                `
              : html`
                  <tr-button
                    ${buttonColorStyle(this._loginColors)}
                    @click=${() => {
                      if (!validateEmailAndSetError(email)) return;
                      this._email = email;
                    }}
                  >
                    ${this._translation.translate('LOGIN_PAGE', 'next')}
                  </tr-button>
                `
          }
          ${
            withoutPassword
              ? html`
                  <tr-button
                    ${outlineButtonColorStyle(this._loginColors)}
                    mode="outline"
                    icon="mail"
                    @click=${() => {
                      const _email = this._email || email;
                      if (!validateEmailAndSetError(_email)) return;
                      emailOnlyLoginCallback!(_email);
                    }}
                  >
                    ${this._translation.translate('LOGIN_PAGE', 'loginWithoutPassword')}
                  </tr-button>
                `
              : nothing
          }

          <!-- same as loginWithoutPassword; This is for users who really want to reset their password and do not understand that they could use the loginWithoutPassword button -->
          <tr-button
            ${outlineButtonColorStyle(this._loginColors)}
            mode="outline"
            icon="mail"
            @click=${() => {
              const _email = this._email || email;
              if (!validateEmailAndSetError(_email)) return;
              emailOnlyLoginCallback!(_email);
            }}
          >
            ${this._translation.translate('LOGIN_PAGE', 'forgotPassword')}
          </tr-button>
        </x-flex>
      </x-flex>
    `;
  }

  private _mailError = '';
  renderLoginOnlyWithMail() {
    return this.loginWithMailRenderHelper({
      withoutPassword: true,
      emailOnlyLoginCallback: (email: string) => this._loginWithEmailOnly(email),
      emailAndPasswordLoginCallback: (email: string, password: string) => {
        return this._loginWithCredentials(email, password);
      },
    });
  }

  renderValidateToken() {
    return html`
      <bh-component-action-otp-login
        .buttonColors=${this._loginColors}
        email=${this._email}
        institution-id=${ifDefined(this._institutionID.value)}
        .buttonColor=${this._loginColors?.buttonColor}
        .buttonFontColor=${this._loginColors?.buttonFontColor}
        @login=${() => this._login()}
      ></bh-component-action-otp-login>
    `;
  }

  private async _loginWithEmailOnly(email?: string | null) {
    email = (email ?? this._email).trim();
    if (!emailRegexCheck(email)) {
      this._mailError = this._translation.translate('LOGIN_PAGE', 'enterValidMailAddress');
      this.requestUpdate();
      return;
    }
    this.mode = 'token_validation';
    this._email = email;
  }

  private async _loginWithCredentials(email: string, password: string) {
    // TODO: validate email
    // TODO: validate password

    this.errorMessage = '';

    if (
      !(await TrDialog.waitFor(
        new Promise<boolean>(async resolve => {
          try {
            await new Promise<void>((resolve, reject) => {
              const authenticationInterface = getBuchungsheldAuthentication().client.createInterface({
                intent: 'login',
                onCompleted: () => resolve(),
                onError: error => reject(error),
              });
              void authenticationInterface.begin('password', { kind: 'password', username: email, password });
            });
          } catch (error) {
            const key = (error as BaseError).extensions?.translationKey;
            this.errorMessage =
              error instanceof InvalidCredentialsError ||
              error instanceof AmbiguousLoginError ||
              key === 'invalid_credentials' ||
              key === 'users.invalid_credentials'
                ? this._translation.translate('LOGIN_PAGE', 'emailOrPasswordWrong')
                : this._translation.translate('LOGIN_PAGE', 'invalidInput');
            return resolve(false); // exit on fail
          }

          updateCurrentUser(undefined, false);
          await subscribeCurrentUser();
          resolve(true);
        }),
      ))
    )
      return;

    // user should be logged in now
    this._login();
  }

  private _login() {
    this.dispatchEvent(new CustomEvent('login'));
  }
}
